/**
 * =============================================================================
 * EDIT DIALOG COMPONENT
 * =============================================================================
 * 
 * Common edit dialog used by all editable Mission Control widgets:
 * - MetricCard
 * - WatchCard
 * - StandaloneWatchCard
 * 
 * LAYOUT:
 * ┌──────────────────────────────────────────────────────────────────┐
 * │  [X]                    Edit: {entityLabel}                      │
 * ├──────────────────────────────────────────────────────────────────┤
 * │  LEFT PANE (40%)               │  RIGHT PANE (60%)               │
 * │  ┌───────────────────────────┐ │  ┌───────────────────────────┐ │
 * │  │  REFERENCE PANEL          │ │  │  AI CHAT INTERFACE        │ │
 * │  │                           │ │  │                           │ │
 * │  │  Title: {reference.title} │ │  │  [Chat messages...]       │ │
 * │  │                           │ │  │                           │ │
 * │  │  Current Value:           │ │  │                           │ │
 * │  │  {reference.currentValue} │ │  │                           │ │
 * │  │                           │ │  │  ┌─────────────────────┐  │ │
 * │  │  Metadata:                │ │  │  │ Describe your edit..│  │ │
 * │  │  • Label: Value           │ │  │  └─────────────────────┘  │ │
 * │  └───────────────────────────┘ │  └───────────────────────────┘ │
 * ├──────────────────────────────────────────────────────────────────┤
 * │                                    [Cancel]  [Save Changes]      │
 * └──────────────────────────────────────────────────────────────────┘
 * 
 * API INTEGRATION:
 * - Chat messages are sent to: config.agentConfig.endpoint
 * - Agent responds with suggested edits
 * - On "Save Changes": calls onSave callback with updated data
 */

import { 
    Modal, 
    Box, 
    Stack, 
    Text, 
    Group, 
    Button, 
    TextInput,
    ScrollArea,
    Loader,
    Badge,
    Divider,
} from '@mantine/core';
import { 
    PencilSimple, 
    X, 
    PaperPlaneTilt,
    Robot,
    User,
    Sparkle,
    Info,
} from '@phosphor-icons/react';
import { memo, useState, useCallback, useRef, useEffect } from 'react';
import { formatMoney, formatCount } from '@utils/currency';
import type { 
    EditDialogProps, 
    EditDialogChatMessage,
    EditDialogConfig,
} from '../../types';

// =============================================================================
// HELPER: Generate unique ID
// =============================================================================
const generateId = (): string => {
    return `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

// =============================================================================
// REFERENCE PANEL COMPONENT
// =============================================================================
interface ReferencePanelProps {
    config: EditDialogConfig;
}

const ReferencePanel: React.FC<ReferencePanelProps> = memo(({ config }) => {
    const { reference, entityType, entityLabel } = config;
    
    // Format value based on type
    const formatValue = (value: string | number | undefined, format?: string): string => {
        if (value === undefined || value === null) return '-';
        if (format === 'currency') return formatMoney(Number(value));
        if (format === 'percentage') return `${value}%`;
        if (format === 'number') return formatCount(value);
        return String(value);
    };
    
    return (
        <Stack className="h-full gap-4">
            {/* Entity type badge */}
            <Group className="gap-2">
                <Badge 
                    size="sm" 
                    variant="light" 
                    color="blue"
                    leftSection={<Info size={12} weight="fill" />}
                >
                    {entityType.replace(/-/g, ' ').toUpperCase()}
                </Badge>
            </Group>
            
            {/* Reference title */}
            <Box>
                <Text className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                    Editing
                </Text>
                <Text className="text-lg font-semibold text-gray-900">
                    {reference.title || entityLabel}
                </Text>
            </Box>
            
            {/* Description */}
            {reference.description && (
                <Text className="text-sm text-gray-600">
                    {reference.description}
                </Text>
            )}
            
            <Divider className="my-2" />
            
            {/* Current value */}
            {reference.currentValue !== undefined && (
                <Box>
                    <Text className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                        Current Value
                    </Text>
                    <Text className="text-2xl font-bold text-blu-800">
                        {formatValue(reference.currentValue, reference.currentValueFormat)}
                    </Text>
                </Box>
            )}
            
            {/* Metadata items */}
            {reference.metadata && reference.metadata.length > 0 && (
                <Box className="mt-2">
                    <Text className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                        Details
                    </Text>
                    <Stack className="gap-2">
                        {reference.metadata.map((item, idx) => (
                            <Group key={idx} className="justify-between">
                                <Text className="text-sm text-gray-600">
                                    {item.label}
                                </Text>
                                <Text className="text-sm font-medium text-gray-900">
                                    {formatValue(item.value, item.format)}
                                </Text>
                            </Group>
                        ))}
                    </Stack>
                </Box>
            )}
            
            {/* Spacer */}
            <Box className="flex-1" />
            
            {/* Help text */}
            <Box className="rounded-lg bg-blu-100/50 p-3 border border-blu-300/30">
                <Group className="gap-2 mb-1">
                    <Sparkle size={14} weight="fill" className="text-blu-800" />
                    <Text className="text-xs font-semibold text-blu-800">
                        AI-Powered Editing
                    </Text>
                </Group>
                <Text className="text-xs text-gray-600">
                    Describe your changes in natural language and the AI will help you make the edits.
                </Text>
            </Box>
        </Stack>
    );
});

ReferencePanel.displayName = 'ReferencePanel';

// =============================================================================
// CHAT MESSAGE COMPONENT
// =============================================================================
interface ChatMessageProps {
    message: EditDialogChatMessage;
}

const ChatMessage: React.FC<ChatMessageProps> = memo(({ message }) => {
    const isUser = message.role === 'user';
    const isSystem = message.role === 'system';
    
    if (isSystem) {
        return (
            <Box className="px-3 py-2 rounded-lg bg-gray-100 text-center">
                <Text className="text-xs text-gray-500">
                    {message.content}
                </Text>
            </Box>
        );
    }
    
    return (
        <Group 
            className={`gap-2 ${isUser ? 'flex-row-reverse' : ''}`}
            align="flex-start"
        >
            {/* Avatar */}
            <Box 
                className={`
                    flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center
                    ${isUser ? 'bg-blu-800' : 'bg-purple/20'}
                `}
            >
                {isUser ? (
                    <User size={14} weight="fill" className="text-white" />
                ) : (
                    <Robot size={14} weight="fill" className="text-purple" />
                )}
            </Box>
            
            {/* Message bubble */}
            <Box 
                className={`
                    max-w-[80%] rounded-2xl px-3 py-2
                    ${isUser 
                        ? 'bg-blu-800 text-white rounded-tr-sm' 
                        : 'bg-gray-100 text-gray-900 rounded-tl-sm'
                    }
                `}
            >
                <Text className="text-sm whitespace-pre-wrap">
                    {message.content}
                </Text>
                {message.isStreaming && (
                    <Box className="mt-1">
                        <Loader size="xs" color={isUser ? 'white' : 'blue'} />
                    </Box>
                )}
            </Box>
        </Group>
    );
});

ChatMessage.displayName = 'ChatMessage';

// =============================================================================
// AI CHAT PANEL COMPONENT
// =============================================================================
interface AIChatPanelProps {
    config: EditDialogConfig;
    messages: EditDialogChatMessage[];
    isLoading: boolean;
    onSendMessage: (content: string) => void;
}

const AIChatPanel: React.FC<AIChatPanelProps> = memo(({ 
    config, 
    messages, 
    isLoading, 
    onSendMessage 
}) => {
    const [inputValue, setInputValue] = useState('');
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    
    // Auto-scroll to bottom on new messages
    useEffect(() => {
        if (scrollAreaRef.current) {
            scrollAreaRef.current.scrollTo({
                top: scrollAreaRef.current.scrollHeight,
                behavior: 'smooth',
            });
        }
    }, [messages]);
    
    // Handle send
    const handleSend = useCallback(() => {
        if (inputValue.trim() && !isLoading) {
            onSendMessage(inputValue.trim());
            setInputValue('');
        }
    }, [inputValue, isLoading, onSendMessage]);
    
    // Handle key press
    const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }, [handleSend]);
    
    return (
        <Stack className="h-full gap-0">
            {/* Chat header */}
            <Box className="pb-3 border-b border-gray-200">
                <Group className="gap-2">
                    <Robot size={18} weight="duotone" className="text-purple" />
                    <Text className="text-sm font-semibold text-gray-900">
                        AI Assistant
                    </Text>
                </Group>
                <Text className="text-xs text-gray-500 mt-1">
                    {config.agentConfig.inputPlaceholder || 'Describe your changes and I\'ll help you make them'}
                </Text>
            </Box>
            
            {/* Messages area */}
            <ScrollArea 
                className="flex-1 py-4" 
                viewportRef={scrollAreaRef}
            >
                <Stack className="gap-3">
                    {messages.length === 0 ? (
                        <Box className="text-center py-8">
                            <Sparkle size={32} weight="duotone" className="text-gray-300 mx-auto mb-2" />
                            <Text className="text-sm text-gray-400">
                                Start a conversation to make edits
                            </Text>
                        </Box>
                    ) : (
                        messages.map((msg) => (
                            <ChatMessage key={msg.id} message={msg} />
                        ))
                    )}
                    
                    {isLoading && (
                        <Group className="gap-2">
                            <Box className="w-7 h-7 rounded-full bg-purple/20 flex items-center justify-center">
                                <Robot size={14} weight="fill" className="text-purple" />
                            </Box>
                            <Box className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-2">
                                <Loader size="xs" color="gray" />
                            </Box>
                        </Group>
                    )}
                </Stack>
            </ScrollArea>
            
            {/* Input area */}
            <Box className="pt-3 border-t border-gray-200">
                <Group className="gap-2">
                    <TextInput
                        ref={inputRef}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyPress}
                        placeholder={config.agentConfig.inputPlaceholder || 'Describe your edit...'}
                        className="flex-1"
                        disabled={isLoading}
                        styles={{
                            input: {
                                borderRadius: '9999px',
                                paddingLeft: '16px',
                                paddingRight: '16px',
                            },
                        }}
                    />
                    <Button
                        variant="filled"
                        color="blue"
                        size="sm"
                        className="rounded-full"
                        onClick={handleSend}
                        disabled={!inputValue.trim() || isLoading}
                        loading={isLoading}
                    >
                        <PaperPlaneTilt size={16} weight="fill" />
                    </Button>
                </Group>
            </Box>
        </Stack>
    );
});

AIChatPanel.displayName = 'AIChatPanel';

// =============================================================================
// MAIN EDIT DIALOG COMPONENT
// =============================================================================
const EditDialog: React.FC<EditDialogProps> = memo(({
    opened,
    config,
    onClose,
    onSave,
    isSaving = false,
}) => {
    // Chat state
    const [messages, setMessages] = useState<EditDialogChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // Reset state when dialog opens/closes
    useEffect(() => {
        if (opened && config) {
            // Add initial system message if configured
            if (config.agentConfig.initialMessage) {
                setMessages([{
                    id: generateId(),
                    role: 'assistant',
                    content: config.agentConfig.initialMessage,
                    timestamp: new Date().toISOString(),
                }]);
            } else {
                setMessages([]);
            }
        } else {
            setMessages([]);
        }
    }, [opened, config]);
    
    // Handle sending a message to the AI
    const handleSendMessage = useCallback(async (content: string) => {
        if (!config) return;
        
        // Add user message
        const userMessage: EditDialogChatMessage = {
            id: generateId(),
            role: 'user',
            content,
            timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, userMessage]);
        
        setIsLoading(true);
        
        try {
            // =================================================================
            // API CALL PLACEHOLDER
            // This is where the actual agent API call will be made
            // =================================================================
            // const response = await fetch(config.agentConfig.endpoint, {
            //     method: config.agentConfig.method,
            //     headers: { 'Content-Type': 'application/json' },
            //     body: JSON.stringify({
            //         message: content,
            //         entityId: config.entityId,
            //         entityType: config.entityType,
            //         ...config.agentConfig.contextParams,
            //     }),
            // });
            // const data = await response.json();
            // =================================================================
            
            // MOCK RESPONSE - Replace with actual API call
            await new Promise((resolve) => setTimeout(resolve, 1000));
            
            const assistantMessage: EditDialogChatMessage = {
                id: generateId(),
                role: 'assistant',
                content: `I understand you want to: "${content}"\n\nI'll help you make this change. Click "Save Changes" when you're ready to apply the updates.`,
                timestamp: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, assistantMessage]);
            
        } catch (error) {
            console.error('Error calling agent:', error);
            
            const errorMessage: EditDialogChatMessage = {
                id: generateId(),
                role: 'assistant',
                content: 'Sorry, I encountered an error. Please try again.',
                timestamp: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    }, [config]);
    
    // Handle save
    const handleSave = useCallback(() => {
        if (onSave) {
            // Extract suggested changes from conversation
            // For now, just pass the messages
            onSave({ messages, entityId: config?.entityId });
        }
        onClose();
    }, [onSave, onClose, messages, config]);
    
    if (!config) return null;
    
    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                <Group className="gap-2">
                    <PencilSimple size={20} weight="fill" className="text-blu-800" />
                    <Text className="font-semibold">
                        Edit: {config.entityLabel}
                    </Text>
                </Group>
            }
            size="xl"
            centered
            padding="lg"
            closeButtonProps={{
                icon: <X size={18} weight="bold" />,
            }}
            styles={{
                content: {
                    maxHeight: '85vh',
                },
                body: {
                    height: '60vh',
                },
            }}
        >
            <Stack className="h-full gap-0">
                {/* Main content: Reference + Chat */}
                <Group className="flex-1 gap-0 min-h-0" align="stretch">
                    {/* Left: Reference Panel (40%) */}
                    <Box 
                        className="w-[40%] pr-4 border-r border-gray-200 overflow-auto"
                        style={{ height: 'calc(60vh - 120px)' }}
                    >
                        <ReferencePanel config={config} />
                    </Box>
                    
                    {/* Right: AI Chat Panel (60%) */}
                    <Box 
                        className="w-[60%] pl-4"
                        style={{ height: 'calc(60vh - 120px)' }}
                    >
                        <AIChatPanel
                            config={config}
                            messages={messages}
                            isLoading={isLoading}
                            onSendMessage={handleSendMessage}
                        />
                    </Box>
                </Group>
                
                {/* Footer: Action buttons */}
                <Box className="pt-4 mt-4 border-t border-gray-200">
                    <Group className="justify-end gap-2">
                        <Button
                            variant="subtle"
                            color="gray"
                            onClick={onClose}
                            disabled={isSaving}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="filled"
                            color="blue"
                            onClick={handleSave}
                            loading={isSaving}
                            disabled={messages.length === 0}
                        >
                            Save Changes
                        </Button>
                    </Group>
                </Box>
            </Stack>
        </Modal>
    );
});

EditDialog.displayName = 'EditDialog';

export default EditDialog;
